import { Checkbox, NativeSelect, Textarea, TextInput } from "@mantine/core";
import {
  AlertCircle,
  Check,
  ExternalLink,
  GitPullRequest,
  LoaderCircle,
  Plus,
} from "lucide-react";
import { useCallback, useRef, useState, type FormEvent } from "react";
import type {
  Activity,
  Member,
  Priority,
  Project,
  Status,
  Task,
} from "../shared/types";
import { taskFlowActions } from "../shared/task-flow";
import AssigneePicker, { assigneeName } from "./AssigneePicker";
import {
  LabelPicker,
  TaskTypePicker,
  useTaskOptions,
} from "./TaskClassification";
import { TaskField, taskPriorityNames } from "./TaskFields";
import { Button, Sheet } from "./ui";

interface Props {
  task?: Task;
  project: Project;
  members: Member[];
  currentUserId?: string;
  mobile?: boolean;
  initialStatus?: Status;
  demo?: boolean;
  activities?: Activity[];
  onSave: (input: any) => Promise<Task | void>;
  onClose: () => void;
}
const errorText = (error: unknown) =>
  error instanceof Error ? error.message : "保存失败，请重试";
function safeLink(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export default function TaskEditor(props: Props) {
  return props.task ? (
    <TaskDetails {...props} task={props.task} />
  ) : (
    <CreateTask {...props} />
  );
}

function TaskDetails({
  task,
  project,
  members,
  currentUserId,
  mobile = false,
  onSave,
  onClose,
  activities = [],
}: Props & { task: Task }) {
  const readOnly = project.role === "viewer" || project.repo_archived;
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [busy, setBusy] = useState(false);
  const writing = useRef(false);
  const revision = useRef(task.revision);
  const [error, setError] = useState("");
  const [openEditors, setOpenEditors] = useState<Set<string>>(() => new Set());
  const onEditingChange = useCallback((id: string, opened: boolean) => {
    setOpenEditors((current) => {
      const next = new Set(current);
      if (opened) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const { options, loading } = useTaskOptions(project.id);
  const dirty = title !== task.title || description !== task.description;
  const commit = async (input: Record<string, unknown>) => {
    if (writing.current) throw new Error("正在保存，请稍后再试");
    writing.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await onSave({
        expected_revision: revision.current,
        ...input,
      });
      if (result) revision.current = result.revision;
      return result;
    } catch (err) {
      setError(errorText(err));
      throw err;
    } finally {
      writing.current = false;
      setBusy(false);
    }
  };
  const saveContent = async (event: FormEvent) => {
    event.preventDefault();
    const input: Record<string, unknown> = {};
    if (title !== task.title) input.title = title;
    if (description !== task.description) input.description = description;
    if (!Object.keys(input).length) return;
    try {
      const saved = await commit(input);
      if (saved) {
        setTitle(saved.title);
        setDescription(saved.description);
      }
    } catch {
      /* The form and field show the actionable error. */
    }
  };
  const fieldProps = {
    task,
    project,
    members,
    currentUserId,
    mobile,
    onEditingChange,
    onCommit: commit,
    disabled: readOnly || busy,
  };
  const identity = task.github_issue_number
    ? `Issue #${task.github_issue_number}`
    : `T-${task.number}`;
  return (
    <Sheet
      title={identity}
      onClose={onClose}
      side
      wide
      trapFocus={!openEditors.size}
      closeOnEscape={!openEditors.size}
    >
      <form
        className="task-detail-form"
        onSubmit={(event) => void saveContent(event)}
      >
        <div className="task-detail-body">
          <div className="task-detail-breadcrumb">
            <span
              className="project-square"
              style={{ background: project.color }}
            />
            {project.name}
            <span className="task-save-note">
              {busy ? "正在保存…" : "属性修改即时保存"}
            </span>
          </div>
          <Textarea
            className="task-detail-title"
            aria-label="任务名称"
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            autosize
            minRows={1}
            maxLength={240}
            required
            disabled={readOnly || busy}
          />
          <div className="task-primary-properties" aria-label="任务主要属性">
            <TaskField {...fieldProps} kind="status" />
            <TaskField {...fieldProps} kind="people" />
            {!readOnly &&
            !busy &&
            currentUserId &&
            task.status !== "in_review" &&
            !task.assignees.length ? (
              <Button
                className="text-button task-assign-self"
                onClick={() =>
                  void commit({ assignee_ids: [currentUserId] }).catch(() => {})
                }
              >
                分配给我
              </Button>
            ) : null}
          </div>
          {readOnly ? (
            <p className="task-field-hint">
              当前为只读权限，可查看任务及关联代码。
            </p>
          ) : null}
          {error ? (
            <div className="form-error" role="alert">
              <AlertCircle size={16} />
              {error}
            </div>
          ) : null}
          <div className="task-detail-layout">
            <div className="task-detail-main">
              <Textarea
                className="task-detail-description"
                label="描述"
                placeholder="补充背景、复现步骤或验收标准…"
                value={description}
                onChange={(event) => setDescription(event.currentTarget.value)}
                autosize
                minRows={7}
                maxRows={24}
                maxLength={20000}
                disabled={readOnly || busy}
              />
              {task.pull_requests.length ? (
                <section className="task-links-section">
                  <h3>
                    关联的 Pull Request <span>{task.pull_requests.length}</span>
                  </h3>
                  {task.pull_requests.map((pr) => (
                    <a
                      key={pr.id}
                      href={safeLink(pr.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <GitPullRequest size={16} />
                      <span>
                        <strong>
                          #{pr.number} {pr.title}
                        </strong>
                        <small>
                          {pr.author} ·{" "}
                          {pr.state === "merged"
                            ? "已合并"
                            : pr.state === "closed"
                              ? "已关闭"
                              : pr.state === "draft"
                                ? "草稿"
                                : "待评审"}
                          {pr.checks ? ` · CI ${pr.checks}` : ""}
                        </small>
                      </span>
                      <ExternalLink size={14} />
                    </a>
                  ))}
                </section>
              ) : null}
              <section className="task-detail-activity">
                <h3>动态</h3>
                {activities.length ? (
                  activities.slice(0, 8).map((activity) => (
                    <article key={activity.id}>
                      <span className="activity-pin" />
                      <div>
                        <p>
                          <strong>{activity.actor}</strong> {activity.title}
                        </p>
                        {activity.detail.includes("\n") ? (
                          <p className="task-activity-detail">
                            {activity.detail.split("\n").slice(1).join("\n")}
                          </p>
                        ) : null}
                        <time dateTime={activity.created_at}>
                          {new Date(activity.created_at).toLocaleString(
                            "zh-CN",
                            {
                              month: "numeric",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </time>
                      </div>
                    </article>
                  ))
                ) : (
                  <p className="task-field-hint">
                    状态、分配和任务修改会记录在这里。
                  </p>
                )}
              </section>
            </div>
            <aside className="task-detail-properties" aria-label="其他任务属性">
              <h3>属性</h3>
              {(["priority", "type", "date", "labels"] as const).map((kind) => (
                <div className="task-property-row" key={kind}>
                  <span>
                    {
                      {
                        priority: "优先级",
                        type: "类型",
                        date: "截止日期",
                        labels: "标签",
                      }[kind]
                    }
                  </span>
                  <TaskField
                    {...fieldProps}
                    kind={kind}
                    options={options}
                    disabled={
                      readOnly ||
                      busy ||
                      (loading && ["type", "labels"].includes(kind))
                    }
                  />
                </div>
              ))}
              {task.status === "in_review" ? (
                <div className="task-owner-caption">
                  <span>任务负责人</span>
                  <strong>
                    {task.assignees.map(assigneeName).join("、") || "未分配"}
                  </strong>
                  <small>测试期间保留原负责人。</small>
                </div>
              ) : null}
              {task.github_url ? (
                <a
                  className="task-repository-link"
                  href={safeLink(task.github_url)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>GitHub #{task.github_issue_number}</span>
                  <ExternalLink size={14} />
                  <small>
                    {task.sync_state === "pending"
                      ? "正在同步"
                      : task.sync_state === "error"
                        ? "同步失败"
                        : "已连接"}
                  </small>
                </a>
              ) : null}
              {task.testing_pr_id &&
              task.github_state === "closed" &&
              task.status === "in_review" ? (
                <p className="task-field-hint">
                  Issue 已随 PR 关闭，任务仍等待测试验收。
                </p>
              ) : project.auto_testing &&
                !["done", "cancelled", "in_review"].includes(task.status) ? (
                <p className="task-field-hint">关联 PR 合并后自动提测。</p>
              ) : null}
              {options.warning ? (
                <p className="task-field-hint">{options.warning}</p>
              ) : null}
            </aside>
          </div>
        </div>
        <footer className="task-detail-footer">
          <span>
            {dirty
              ? "文字内容尚未保存"
              : readOnly
                ? "当前为只读权限"
                : "状态和分配会同步更新"}
          </span>
          <Button className="button secondary" onClick={onClose}>
            返回
          </Button>
          {!readOnly ? (
            dirty ? (
              <Button
                type="submit"
                className="button primary"
                disabled={busy || !title.trim()}
              >
                {busy ? (
                  <LoaderCircle size={15} className="loading-spin" />
                ) : (
                  <Check size={15} />
                )}
                保存内容
              </Button>
            ) : (
              <TaskField
                {...fieldProps}
                kind="status"
                action={taskFlowActions[task.status].primary}
              />
            )
          ) : null}
        </footer>
      </form>
    </Sheet>
  );
}

function CreateTask({
  project,
  members,
  currentUserId,
  initialStatus = "todo",
  demo,
  onSave,
  onClose,
}: Props) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [assignees, setAssignees] = useState<string[]>(
    currentUserId ? [currentUserId] : [],
  );
  const [date, setDate] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [issueType, setIssueType] = useState<string | null>(null);
  const [sync, setSync] = useState(Boolean(project.repo_id));
  const [busy, setBusy] = useState(false);
  const writing = useRef(false);
  const [error, setError] = useState("");
  const requestId = useRef(crypto.randomUUID());
  const { options, loading } = useTaskOptions(project.id);
  const readOnly = project.role === "viewer" || project.repo_archived;
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (writing.current) return;
    writing.current = true;
    setBusy(true);
    setError("");
    try {
      await onSave({
        title,
        description,
        status: initialStatus,
        priority,
        assignee_ids: assignees,
        tester_ids: [],
        due_date: date || null,
        labels,
        issue_type: issueType,
        request_id: requestId.current,
        sync_to_github: sync,
      });
    } catch (err) {
      setError(errorText(err));
    } finally {
      writing.current = false;
      setBusy(false);
    }
  };
  return (
    <Sheet title="新建任务" onClose={onClose} side>
      <form
        onSubmit={(event) => void save(event)}
        className="editor-form task-create-form"
      >
        <div className="editor-body">
          <div className="editor-project">
            <span
              className="project-square"
              style={{ background: project.color }}
            />
            {project.name}
          </div>
          <fieldset
            className="editor-fields"
            aria-label="新任务内容"
            disabled={readOnly || busy}
          >
            <Textarea
              className="ui-form-field task-create-title"
              label="任务名称"
              required
              autosize
              minRows={1}
              maxLength={240}
              placeholder="这件事要完成什么？"
              value={title}
              onChange={(event) => setTitle(event.currentTarget.value)}
            />
            <AssigneePicker
              label="负责人"
              members={members}
              selected={assignees}
              onChange={setAssignees}
              disabled={readOnly || busy}
            />
            <Textarea
              className="ui-form-field ui-form-description"
              label="描述"
              minRows={5}
              autosize
              maxRows={12}
              maxLength={20000}
              value={description}
              onChange={(event) => setDescription(event.currentTarget.value)}
              placeholder="补充背景、复现步骤或验收标准…"
            />
            <div className="form-grid">
              <NativeSelect
                className="ui-form-field"
                label="优先级"
                value={priority}
                onChange={(event) =>
                  setPriority(event.currentTarget.value as Priority)
                }
              >
                {Object.entries(taskPriorityNames).map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </NativeSelect>
              <TaskTypePicker
                value={issueType}
                onChange={setIssueType}
                options={options}
                disabled={readOnly || loading}
                repository={!demo && Boolean(project.repo_id)}
              />
              <TextInput
                className="ui-form-field"
                label="截止日期"
                type="date"
                value={date}
                onChange={(event) => setDate(event.currentTarget.value)}
              />
            </div>
            <LabelPicker
              value={labels}
              onChange={setLabels}
              options={options.labels}
              disabled={readOnly}
            />
            {options.warning ? (
              <p className="settings-hint">{options.warning}</p>
            ) : null}
            {project.repo_id ? (
              <Checkbox
                className="ui-sync-choice"
                label="同时创建 GitHub Issue"
                checked={sync}
                onChange={(event) => setSync(event.currentTarget.checked)}
              />
            ) : null}
          </fieldset>
          {error ? (
            <div className="form-error" role="alert">
              <AlertCircle size={15} />
              {error}
            </div>
          ) : null}
        </div>
        <div className="dialog-footer">
          <Button className="button secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            type="submit"
            className="button primary"
            disabled={busy || readOnly || !title.trim()}
          >
            {busy ? (
              <LoaderCircle size={15} className="loading-spin" />
            ) : (
              <Plus size={15} />
            )}
            创建任务
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
